import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useParams } from 'react-router-dom'
import MainLayout from 'Hoc/MainLayout'
import { fetchDepartments, fetchFamilies, fetchStores } from 'Actions/data-actions'
import Loader from 'Components/Loader'
import { EditDossierHeaderModal } from 'Components/EditDossierHeaderModal'
import api from 'Common/api'
import { Descriptions, Popconfirm, Button, Alert, Tag } from 'antd'
import {
  RULE_PURPOSE,
  RULE_ACTION,
  evaluateRule,
  getPromoDurationInDays,
} from 'Common/promoLocaliRules'
import {
  getDossierTitle,
  formatDate,
  handleServerSuccess,
  handleServerError,
  getPromoLocaliStatoMeta,
} from 'Common/utils'
import { StyledDescriptions, StyledDiv, StyledButton, StyledTitle } from './styled'
import ErrorPage from 'Components/ErrorPage'
import PromoLocali from 'Components/PromoLocali'
import appAccess from 'Common/appAccess'
import { dossierTypes, dossierStates } from 'Common/constants'
import {
  ROLE_CS,
  TITLE_STORE_PERFORMANCE_LEADER,
  TITLE_SUPPLY_DEMAND_MANAGER,
  TITLE_CAPO_SETTORE_SUPPLY_CHAIN,
} from 'Common/constants/global'

// The Promo Locali API exposes a `stato` enum (DRAFT/CONFIRMED/INSTALLED/REJECTED/
// CANCELLED). We only tag the header with its dossier `type` so the shared
// appAccess logic can branch on it; the `stato` enum is used as-is.
const toHeader = (dto) => ({
  ...dto,
  type: 'PL',
})

const toHeaderDto = (header) => {
  const { type, ...rest } = header
  return rest
}

// Missions that are allowed to validate a Promo Locale that triggered a warning.
const SPECIAL_MISSIONS = [
  TITLE_SUPPLY_DEMAND_MANAGER,
  TITLE_STORE_PERFORMANCE_LEADER,
  TITLE_CAPO_SETTORE_SUPPLY_CHAIN,
]

const SPL_CHECK_HINT = 'è richiesto un controllo da parte di uno Store Performance Leader (SPL) o di un CS Libero Servizio.'
const SPECIAL_MISSION_WARN_HINT = 'verifica la promo e, se corretta, confermala.'

const BLOCK_HINTS = {
  [RULE_PURPOSE.TOTAL_REF]: 'la promo non può essere confermata.',
  [RULE_PURPOSE.DURATION]: 'la promo non può essere confermata.',
}

const WARN_MESSAGES = {
  [RULE_PURPOSE.TOTAL_REF]: 'Numero di referenze elevato',
  [RULE_PURPOSE.DURATION]: 'Durata della promo elevata',
}

const BLOCK_MESSAGES = {
  [RULE_PURPOSE.TOTAL_REF]: 'Numero di referenze troppo elevato',
  [RULE_PURPOSE.DURATION]: 'Durata della promo troppo elevata',
}

const buildRuleAlert = (purpose, rule, isSpecialMission) => {
  if (!rule || rule.action === RULE_ACTION.OK) {
    return null
  }

  if (rule.action === RULE_ACTION.WARN) {
    const hint = isSpecialMission ? SPECIAL_MISSION_WARN_HINT : SPL_CHECK_HINT
    return {
      type: 'warning',
      message: `${WARN_MESSAGES[purpose]}: ${hint}`,
    }
  }

  return {
    type: 'error',
    message: `${BLOCK_MESSAGES[purpose]}: ${BLOCK_HINTS[purpose]}`,
  }
}

const EditPromoLocali = () => {
  const { id } = useParams()

  const dispatch = useDispatch()
  const departments = useSelector((state) => state.Data.departments)
  const families = useSelector((state) => state.Data.families)
  const stores = useSelector((state) => state.Data.stores)
  const user = useSelector((state) => state.User)
  const features = useSelector((state) => state.Data.features)

  const tableRef = useRef(null)
  const headerModalRef = useRef(null)

  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState(null)
  const [redirectTo, setRedirectTo] = useState(null)
  const [dossierHeader, setDossierHeader] = useState(null)
  const [isHeaderModalVisible, setIsHeaderModalVisible] = useState(false)
  const [isSavingHeader, setIsSavingHeader] = useState(false)
  const [isDeletingDossier, setIsDeletingDossier] = useState(false)
  const [rules, setRules] = useState([])
  const [productCount, setProductCount] = useState(null)
  const [isChangingStato, setIsChangingStato] = useState(false)
  const [isRequestingConfirmation, setIsRequestingConfirmation] = useState(false)

  useEffect(() => {
    let cancelled = false

    setIsLoading(true)
    setStatus(null)
    setRedirectTo(null)
    setDossierHeader(null)
    setRules([])
    setProductCount(null)

    const promises = [
      dispatch(fetchDepartments()),
      dispatch(fetchFamilies()),
    ]

    if (stores.length === 0) {
      promises.push(dispatch(fetchStores()))
    }

    Promise.all(promises)
      .then(() => Promise.all([api.promoLocali.getOne(id), api.promoLocali.getRules()]))
      .then(([result, rulesResult]) => {
        if (cancelled) {
          return
        }

        if (!result.data) {
          setStatus('404')
          setIsLoading(false)
          return
        }

        if (!dossierTypes.filter((type) => type.key === 'PL')[0].canView.includes(user.role)) {
          setStatus('403')
          setIsLoading(false)
          return
        }

        setRules(rulesResult.data || [])
        setDossierHeader(toHeader(result.data))
        setIsLoading(false)
      })
      .catch((error) => {
        if (cancelled) {
          return
        }

        const errorStatus = error.response ? error.response.status : null
        if (errorStatus === 404) {
          setStatus('404')
        } else if (errorStatus >= 500 && errorStatus < 600) {
          setStatus('500')
        } else {
          setStatus('error')
        }
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [dispatch, id, stores.length, user.role])

  const toggleHeaderModal = () => {
    setIsHeaderModalVisible((prev) => !prev)
  }

  const saveHeader = () => {
    const { form } = headerModalRef.current.props
    form.validateFields((err, values) => {
      if (!err) {
        const installation = values.installation ? {
          installationFrom: formatDate(values.installation[0]),
          installationTo: formatDate(values.installation[1]),
        } : values.installationTo ? {
          installationTo: formatDate(values.installationTo),
        } : undefined
        const merchArrivalDate = values.merchArrivalDate && {
          merchArrivalDate: formatDate(values.merchArrivalDate),
        }

        setIsSavingHeader(true)
        api.promoLocali
          .update(id, toHeaderDto({ ...dossierHeader, ...installation, ...merchArrivalDate }))
          .then((response) => {
            handleServerSuccess()
            setDossierHeader(toHeader(response.data))
          }, handleServerError)
          .finally(() => {
            setIsSavingHeader(false)
            setIsHeaderModalVisible(false)
          })
      }
    })
  }

  const changePromoStato = async (stato) => {
    setIsChangingStato(true)

    try {
      const response = await api.promoLocali.update(id, { ...toHeaderDto(dossierHeader), stato })
      handleServerSuccess()
      setDossierHeader(toHeader(response.data))
    } catch (e) {
      handleServerError(e)
    } finally {
      setIsChangingStato(false)
    }
  }

  const requestPromoConfirmation = async () => {
    setIsRequestingConfirmation(true)

    try {
      await api.promoLocali.requestConfirmation(id)
      const response = await api.promoLocali.update(id, { ...toHeaderDto(dossierHeader), stato: 'WAITING_CONFIRMATION' })
      handleServerSuccess()
      setDossierHeader(toHeader(response.data))
    } catch (e) {
      handleServerError(e)
    } finally {
      setIsRequestingConfirmation(false)
    }
  }

  const deleteDossier = async () => {
    setIsDeletingDossier(true)

    try {
      await api.promoLocali.delete(id)
      handleServerSuccess()
      setRedirectTo('/promo-locali/search')
    } catch (e) {
      handleServerError(e)
    } finally {
      setIsDeletingDossier(false)
    }
  }

  if (redirectTo !== null) {
    return (
      <Redirect
        push
        to={{
          pathname: redirectTo,
          state: { referrer: '/edit-promo-locali' },
        }}
      />
    )
  }

  if (status) {
    return <ErrorPage status={status} />
  }

  if (isLoading || !dossierHeader) {
    return <Loader />
  }

  const totalRefRule = evaluateRule(rules, RULE_PURPOSE.TOTAL_REF, productCount)
  const durationRule = evaluateRule(
    rules,
    RULE_PURPOSE.DURATION,
    getPromoDurationInDays(dossierHeader.installationFrom, dossierHeader.installationTo),
  )

  const isSpecialMission = SPECIAL_MISSIONS.includes(user.mission)

  const showRuleAlerts = dossierHeader.stato === 'DRAFT'

  const totalRefAlert = showRuleAlerts ? buildRuleAlert(RULE_PURPOSE.TOTAL_REF, totalRefRule, isSpecialMission) : null
  const durationAlert = showRuleAlerts ? buildRuleAlert(RULE_PURPOSE.DURATION, durationRule, isSpecialMission) : null

  const ruleActions = [totalRefRule, durationRule]
    .filter(Boolean)
    .map((rule) => rule.action)
  const hasBlock = ruleActions.includes(RULE_ACTION.BLOCK)
  const hasWarn = ruleActions.includes(RULE_ACTION.WARN)
  const hasNoAlerts = !hasWarn && !hasBlock

  const canManuallyAct = user.role === ROLE_CS
    && productCount !== null
    && dossierHeader.stato === 'DRAFT'

  const isOutOfStoreSPL = user.mission === TITLE_STORE_PERFORMANCE_LEADER
    && user.store !== dossierHeader.idStore

  const canConfirm = canManuallyAct && !hasBlock && (hasNoAlerts || isSpecialMission) && !isOutOfStoreSPL
  const canReject = canManuallyAct && isSpecialMission && hasWarn && !hasBlock
  const canRequestConfirmation = canManuallyAct && !isSpecialMission && !canConfirm && !hasBlock

  return (
    <MainLayout
      tourSteps={[]}
      isTourAvailable={false}
      isTourOpen={false}
      defaultCurrentPage="dossier"
    >
      <StyledDescriptions
        title={(
          <StyledDiv>
            <Button
              icon="left"
              onClick={() => setRedirectTo('/promo-locali/search')}
            >
              Indietro
            </Button>
            <StyledTitle style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {dossierHeader.description} [{dossierHeader.id}]
              <div style={{ display: 'flex', gap: 4 }}>
                <Tag>{dossierHeader.year}</Tag>
                <Tag>{dossierHeader.department.toString().padStart(2, '0') } - {departments.find((d) => d.id === dossierHeader.department)?.label}</Tag>
                <Tag>{dossierHeader.idStore} - {stores.find((s) => s.id === dossierHeader.idStore)?.name}</Tag>
              </div>
            </StyledTitle>
            {appAccess.isAuthorized('delete_dossier', {
              stato: dossierHeader.stato,
              type: dossierHeader.type,
              creator: dossierHeader.creationBy,
              installationFrom: dossierHeader.installationFrom,
              installationTo: dossierHeader.installationTo,
            }) && (
              <Popconfirm
                title="Sei sicuro di voler annullare il dossier?"
                onConfirm={deleteDossier}
                okText="Sì"
                cancelText="No"
              >
                <StyledButton type="danger" loading={isDeletingDossier}>
                  Annulla dossier
                </StyledButton>
              </Popconfirm>
            )}
            {appAccess.isAuthorized('edit_dossier_header', {
              stato: dossierHeader.stato,
              type: dossierHeader.type,
              creator: dossierHeader.creationBy,
              installationTo: dossierHeader.installationTo,
            }) && (
              <StyledButton onClick={toggleHeaderModal}>
                Modifica testata
              </StyledButton>
            )}
            {canConfirm && (
              <StyledButton
                type="primary"
                loading={isChangingStato}
                onClick={() => changePromoStato('CONFIRMED')}
              >
                Conferma
              </StyledButton>
            )}
            {canRequestConfirmation && (
              <StyledButton
                type="primary"
                loading={isRequestingConfirmation}
                onClick={requestPromoConfirmation}
              >
                Richiedi Conferma
              </StyledButton>
            )}
            {canReject && (
              <Popconfirm
                title="Sei sicuro di voler rifiutare la promo?"
                onConfirm={() => changePromoStato('REJECTED')}
                okText="Sì"
                cancelText="No"
              >
                <StyledButton type="danger" loading={isChangingStato}>
                  Rifiuta
                </StyledButton>
              </Popconfirm>
            )}
          </StyledDiv>
        )}
        size="small"
        bordered
        column={{ xs: 1, sm: 2, md: 3, xl: 4 }}
      >
        <Descriptions.Item label="Data arrivo merce">
          {formatDate(dossierHeader.merchArrivalDate, 'YYYY-MM-DD', 'DD/MM/YYYY')}
        </Descriptions.Item>
        <Descriptions.Item
          label={dossierTypes.filter((type) => type.key === dossierHeader.type)[0].installationLabel}
        >
          {`${formatDate(dossierHeader.installationFrom, 'YYYY-MM-DD', 'DD/MM/YYYY')} ~ ${formatDate(dossierHeader.installationTo, 'YYYY-MM-DD', 'DD/MM/YYYY')}`}
        </Descriptions.Item>
        <Descriptions.Item label="Stato">
          {getPromoLocaliStatoMeta(dossierHeader.stato).label}
        </Descriptions.Item>
      </StyledDescriptions>
      {(totalRefAlert || durationAlert) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16, marginBottom: 16 }}>
          {totalRefAlert && (
            <Alert
              showIcon
              type={totalRefAlert.type}
              message={totalRefAlert.message}
            />
          )}
          {durationAlert && (
            <Alert
              showIcon
              type={durationAlert.type}
              message={durationAlert.message}
            />
          )}
        </div>
      )}
      <PromoLocali
        ref={tableRef}
        dossier={dossierHeader}
        user={user}
        departments={departments}
        families={families}
        stores={stores}
        features={features ?? {}}
        onProductCountChange={setProductCount}
      />
      <EditDossierHeaderModal
        wrappedComponentRef={headerModalRef}
        visible={isHeaderModalVisible}
        onCancel={toggleHeaderModal}
        onSave={saveHeader}
        dossierHeader={dossierHeader}
        isSavingHeader={isSavingHeader}
        user={user}
      />
    </MainLayout>
  )
}

export default EditPromoLocali
